/* import __COLOCATED_TEMPLATE__ from './move-content-modal.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { get } from 'embercom/lib/ajax';
import type SuggestionService from 'embercom/services/suggestion-service';
import type { Block } from '@intercom/interblocks.ts';

interface RelatedContentResponse {
  entity_id: string;
  entity_type: string;
  passage_blocks: Block[];
  title: string;
}

interface Args {
  reviewRequest: ContentReviewRequest;
  onClose: () => void;
  onMoveRequested: (entity_id: string | number, entity_type: string) => void;
  isOpen: boolean;
}

export default class MoveContentModal extends Component<Args> {
  @service declare notificationsService: $TSFixMe;
  @service declare suggestionService: SuggestionService;
  @service declare appService: $TSFixMe;

  @tracked selectedContent: RelatedContentResponse | null = null;
  @tracked relatedContent: RelatedContentResponse[] = [];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.fetchRelatedContent).perform();
  }

  @dropTask
  *fetchRelatedContent(): Generator<
    Promise<RelatedContentResponse[]>,
    void,
    RelatedContentResponse[]
  > {
    let response = yield get(
      `/ember/content_service/content_review_requests/${this.args.reviewRequest.get('id')}/related_content`,
      {
        top_k: 10,
        app_id: this.appService.app.id,
      },
    );
    this.relatedContent = response;
  }

  @action
  selectContent(content: RelatedContentResponse) {
    this.selectedContent = content;
  }

  @action
  moveContent() {
    if (!this.selectedContent) {
      return;
    }

    //Intentionally not waiting on this task, we want to move content in the background and notify the UI in the meantime
    taskFor(this.suggestionService.replaceReviewRequest).perform(
      this.args.reviewRequest.id,
      this.selectedContent.entity_id,
      this.selectedContent.entity_type,
    );

    this.args.onMoveRequested(this.selectedContent.entity_id, this.selectedContent.entity_type);
  }

  get canMoveContent() {
    return (
      this.selectedContent &&
      !taskFor(this.suggestionService.replaceReviewRequest).isRunning &&
      !taskFor(this.fetchRelatedContent).isRunning
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::MoveContentModal': typeof MoveContentModal;
    'knowledge-hub/move-content-modal': typeof MoveContentModal;
  }
}
